import { ChangeEvent, useCallback, useRef, useState } from 'react'

import { applyDiscount } from '~/modules/sales/services'
import { Discount } from '~/modules/sales/types'
import { insertToast } from '~/modules/toasts/services'
import { debouncer } from '~/utils/debounce'

const debounce = debouncer()
let controller = new AbortController()

type Props = {
  onGetCoupon(coupon?: string): void
  onGetDiscount(discount?: Discount): void
  planId: string
}

export const DiscountInput: React.FC<Props> = ({
  planId,
  onGetCoupon,
  onGetDiscount,
}) => {
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<string>()
  const inputRef = useRef<HTMLInputElement>(null)

  const handleCouponInsert = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const code = e.target.value.trim().toUpperCase()
      inputRef.current!.value = code

      controller.abort()
      controller = new AbortController()

      debounce(async () => {
        if (!code || code.length < 3) {
          onGetDiscount()
          onGetCoupon()
          return
        }
        setLoading(() => true)
        applyDiscount({ code, planId }, controller)
          .then((data) => {
            if (data) {
              onGetDiscount(data)
              onGetCoupon(code)
            } else {
              onGetDiscount()
              onGetCoupon(code)
            }
            setError('')
          })
          .catch((e: Error) => {
            onGetDiscount()
            onGetCoupon()
            insertToast({
              duration: 4,
              message: e.message,
              title: 'Ops',
              type: 'error',
            })
            setError(e.message)
          })
          .finally(() => {
            setLoading(() => false)
          })
      }, 1000)
    },
    [onGetDiscount, onGetCoupon, planId],
  )

  return (
    <div className='flex flex-col duration-200'>
      <span>Aplicar cupom de desconto</span>
      <label htmlFor='discount_input' className='duration-150 overflow-hidden'>
        <div
          data-error={!!error}
          className='flex flex-1 px-2 my-2 gap-2 rounded-md bg-white h-10
            focus-within:border-2 border-2 focus-within:border-gpa-blue-500
            data-[error=true]:border-gpa-red'
        >
          <input
            ref={inputRef}
            id='discount_input'
            className='outline-none flex-1 bg-transparent'
            type='text'
            onChange={handleCouponInsert}
          />
          <div
            data-loading={loading}
            className='p-2 hidden data-[loading=true]:block'
          >
            <div
              className='h-5 w-5 border-2 rounded-full border-gpa-blue-500
              border-b-gpa-blue-50 animate-spin'
            ></div>
          </div>
        </div>
        <p
          data-error={!!error}
          className='data-[error=true]:text-gpa-red data-[error=false]:hidden'
        >
          {error}
        </p>
      </label>
    </div>
  )
}
